import { Business } from '@/api/models';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import {
  useApplyRefresh,
  useFetchRefreshPreview,
  type CachedMapResponse,
  type RefreshComparison,
} from '@/pages/businesses/hooks/useBusinessRefresh';
import {
  mdiAlertCircle,
  mdiCheck,
  mdiCheckboxBlankOutline,
  mdiCheckboxMarked,
  mdiClockOutline,
  mdiImage,
  mdiMapMarker,
  mdiRefresh,
} from '@mdi/js';
import * as React from 'react';

interface BusinessRefreshTabProps {
  business: Business;
}

interface OpeningHoursEntry {
  day: number;
  open: string;
  close: string;
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const BusinessRefreshTab: React.FC<BusinessRefreshTabProps> = ({
  business,
}) => {
  const [selectedChanges, setSelectedChanges] = React.useState<Set<string>>(
    new Set(),
  );
  const [comparison, setComparison] = React.useState<RefreshComparison | null>(
    null,
  );
  const [cachedResponse, setCachedResponse] =
    React.useState<CachedMapResponse | null>(null);
  const [lastAppliedAt, setLastAppliedAt] = React.useState<string | null>(null);
  const [appliedChangesCount, setAppliedChangesCount] = React.useState(0);
  const [lastProviderName, setLastProviderName] = React.useState<string | null>(
    null,
  );

  const fetchPreviewMutation = useFetchRefreshPreview();
  const applyRefreshMutation = useApplyRefresh();

  // Get provider name from comparison or use default
  const providerName = comparison?.provider?.name || 'Map Provider';

  const handleCheckForUpdates = async () => {
    if (!business.id) {
      return;
    }

    // Reset success state when checking for new updates
    setLastAppliedAt(null);
    setAppliedChangesCount(0);
    const result = await fetchPreviewMutation.mutateAsync(business.id);
    setComparison(result);
    if (result.cachedResponse) {
      setCachedResponse(result.cachedResponse);
    }
    setSelectedChanges(new Set());
  };

  const isLoading = fetchPreviewMutation.isPending;
  const error = fetchPreviewMutation.error;

  const toggleChange = (key: string) => {
    setSelectedChanges(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleApplyChanges = async () => {
    if (!business.id) return;

    const changesCount = selectedChanges.size;
    const currentProviderName = providerName;

    await applyRefreshMutation.mutateAsync({
      businessId: business.id,
      providerName: currentProviderName,
      applyName: selectedChanges.has('name'),
      applyAddress: selectedChanges.has('address'),
      applyPhone: selectedChanges.has('phone'),
      applyWebsite: selectedChanges.has('website'),
      applyOpeningHours: selectedChanges.has('openingHours'),
      addNewPhotos: selectedChanges.has('photos'),
      cachedResponse: cachedResponse ?? undefined,
    });

    // Show success state instead of resetting to initial
    setSelectedChanges(new Set());
    setComparison(null);
    setCachedResponse(null);
    setLastAppliedAt(new Date().toISOString());
    setAppliedChangesCount(changesCount);
    setLastProviderName(currentProviderName);
  };

  // Success state - show after applying changes
  if (!comparison && !isLoading && !error && lastAppliedAt) {
    const displayProviderName = lastProviderName || 'Map Provider';
    return (
      <div className='p-6 space-y-6'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center space-x-2'>
            <Icon path={mdiMapMarker} size={24} className='text-blue-600' />
            <div>
              <h3 className='text-lg font-medium text-gray-900'>
                Sync with Map Provider
              </h3>
              <p className='text-xs text-gray-500'>
                Source: {displayProviderName}
              </p>
            </div>
          </div>
        </div>

        <div className='bg-green-50 border border-green-200 rounded-lg p-4'>
          <div className='flex items-center space-x-3'>
            <Icon path={mdiCheck} size={24} className='text-green-600' />
            <div>
              <h4 className='font-medium text-green-800'>
                Changes Applied Successfully
              </h4>
              <p className='text-sm text-green-700'>
                {appliedChangesCount} change
                {appliedChangesCount !== 1 ? 's' : ''} applied from{' '}
                {displayProviderName}
              </p>
              <p className='text-xs text-gray-500 mt-1'>
                Synced: {new Date(lastAppliedAt).toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <Button
          label='Check for More Updates'
          icon={mdiRefresh}
          color='info'
          outline
          onClick={handleCheckForUpdates}
        />
      </div>
    );
  }

  // Initial state - show check for updates button
  if (!comparison && !isLoading && !error) {
    return (
      <div className='p-6 space-y-6'>
        <div className='flex items-center space-x-2'>
          <Icon path={mdiMapMarker} size={24} className='text-blue-600' />
          <h3 className='text-lg font-medium text-gray-900'>
            Sync with Map Provider
          </h3>
        </div>

        <div className='bg-gray-50 rounded-lg p-6 text-center'>
          <Icon
            path={mdiRefresh}
            size={48}
            className='text-gray-400 mx-auto mb-4'
          />
          <p className='text-gray-600 mb-4'>
            Check for updates from a map provider to see if any business
            information has changed.
          </p>
          <Button
            label='Check for Updates'
            icon={mdiRefresh}
            color='info'
            onClick={handleCheckForUpdates}
          />
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className='p-6 flex flex-col items-center justify-center space-y-4'>
        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600' />
        <p className='text-gray-600'>Fetching data from map provider...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className='p-6 space-y-4'>
        <div className='bg-yellow-50 border border-yellow-200 rounded-lg p-4'>
          <div className='flex items-start space-x-3'>
            <Icon
              path={mdiAlertCircle}
              size={24}
              className='text-yellow-600 flex-shrink-0'
            />
            <div>
              <h4 className='font-medium text-yellow-800'>
                Cannot Refresh Business
              </h4>
              <p className='text-sm text-yellow-700 mt-1'>
                This business may not have a map provider reference, or the map
                provider API is not configured.
              </p>
            </div>
          </div>
        </div>
        <Button
          label='Try Again'
          icon={mdiRefresh}
          color='info'
          outline
          onClick={handleCheckForUpdates}
        />
      </div>
    );
  }

  if (!comparison) {
    return null;
  }

  const renderChangeItem = (
    key: string,
    label: string,
    oldValue: string | number | undefined,
    newValue: string | number | undefined,
  ) => {
    const isSelected = selectedChanges.has(key);
    return (
      <div
        key={key}
        className={`border rounded-lg p-4 cursor-pointer transition-colors ${
          isSelected
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-200 hover:border-gray-300'
        }`}
        onClick={() => toggleChange(key)}
      >
        <div className='flex items-start space-x-3'>
          <Icon
            path={isSelected ? mdiCheckboxMarked : mdiCheckboxBlankOutline}
            size={20}
            className={isSelected ? 'text-blue-600' : 'text-gray-400'}
          />
          <div className='flex-1 min-w-0'>
            <p className='font-medium text-gray-900'>{label}</p>
            <div className='mt-2 space-y-1'>
              <div className='flex items-start'>
                <span className='text-xs text-gray-500 w-12 flex-shrink-0'>
                  Old:
                </span>
                <span className='text-sm text-red-600 line-through'>
                  {oldValue || '(empty)'}
                </span>
              </div>
              <div className='flex items-start'>
                <span className='text-xs text-gray-500 w-12 flex-shrink-0'>
                  New:
                </span>
                <span className='text-sm text-green-600'>
                  {newValue || '(empty)'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderOpeningHoursChange = (comparison: RefreshComparison) => {
    if (!comparison.openingHoursChange) return null;

    const isSelected = selectedChanges.has('openingHours');
    const oldHours = (comparison.openingHoursChange.old ||
      []) as OpeningHoursEntry[];
    const newHours = (comparison.openingHoursChange.new ||
      []) as OpeningHoursEntry[];

    return (
      <div
        className={`border rounded-lg p-4 cursor-pointer transition-colors ${
          isSelected
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-200 hover:border-gray-300'
        }`}
        onClick={() => toggleChange('openingHours')}
      >
        <div className='flex items-start space-x-3'>
          <Icon
            path={isSelected ? mdiCheckboxMarked : mdiCheckboxBlankOutline}
            size={20}
            className={isSelected ? 'text-blue-600' : 'text-gray-400'}
          />
          <div className='flex-1'>
            <div className='flex items-center space-x-2'>
              <Icon
                path={mdiClockOutline}
                size={16}
                className='text-gray-500'
              />
              <p className='font-medium text-gray-900'>Opening Hours</p>
            </div>
            <div className='mt-3 grid grid-cols-2 gap-4'>
              <div>
                <p className='text-xs text-gray-500 mb-2'>Current Hours</p>
                {oldHours.length === 0 ? (
                  <p className='text-sm text-gray-400'>No hours set</p>
                ) : (
                  <div className='space-y-1'>
                    {oldHours.map((h, i) => (
                      <p key={i} className='text-xs text-gray-600'>
                        {DAY_NAMES[h.day]}: {h.open} - {h.close}
                      </p>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <p className='text-xs text-gray-500 mb-2'>
                  {providerName} Hours
                </p>
                {newHours.length === 0 ? (
                  <p className='text-sm text-gray-400'>No hours found</p>
                ) : (
                  <div className='space-y-1'>
                    {newHours.map((h, i) => (
                      <p key={i} className='text-xs text-green-600'>
                        {DAY_NAMES[h.day]}: {h.open} - {h.close}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderPhotosChange = (comparison: RefreshComparison) => {
    if (comparison.newPhotosCount === 0) return null;

    const isSelected = selectedChanges.has('photos');

    return (
      <div
        className={`border rounded-lg p-4 cursor-pointer transition-colors ${
          isSelected
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-200 hover:border-gray-300'
        }`}
        onClick={() => toggleChange('photos')}
      >
        <div className='flex items-start space-x-3'>
          <Icon
            path={isSelected ? mdiCheckboxMarked : mdiCheckboxBlankOutline}
            size={20}
            className={isSelected ? 'text-blue-600' : 'text-gray-400'}
          />
          <div className='flex-1'>
            <div className='flex items-center space-x-2'>
              <Icon path={mdiImage} size={16} className='text-gray-500' />
              <p className='font-medium text-gray-900'>New Photos Available</p>
            </div>
            <p className='text-sm text-green-600 mt-1'>
              {comparison.newPhotosCount} new photo
              {comparison.newPhotosCount > 1 ? 's' : ''} from {providerName}
            </p>
            <p className='text-xs text-gray-500 mt-1'>
              Photos will be added without replacing existing ones
            </p>
          </div>
        </div>
      </div>
    );
  };

  const hasAnyChanges =
    comparison.hasChanges ||
    comparison.nameChange ||
    comparison.addressChange ||
    comparison.phoneChange ||
    comparison.websiteChange ||
    comparison.openingHoursChange ||
    comparison.newPhotosCount > 0;

  return (
    <div className='p-6 space-y-6'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div className='flex items-center space-x-2'>
          <Icon path={mdiMapMarker} size={24} className='text-blue-600' />
          <div>
            <h3 className='text-lg font-medium text-gray-900'>
              Sync with Map Provider
            </h3>
            <p className='text-xs text-gray-500'>Source: {providerName}</p>
          </div>
        </div>
        <Button
          label='Check Again'
          icon={mdiRefresh}
          color='info'
          small
          outline
          onClick={handleCheckForUpdates}
          disabled={isLoading}
        />
      </div>

      {/* Last Synced */}
      {comparison.lastSyncedAt && (
        <p className='text-sm text-gray-500'>
          Last synced: {new Date(comparison.lastSyncedAt).toLocaleString()}
        </p>
      )}

      {/* No Changes Message */}
      {!hasAnyChanges && (
        <div className='bg-green-50 border border-green-200 rounded-lg p-4'>
          <div className='flex items-center space-x-3'>
            <Icon path={mdiCheck} size={24} className='text-green-600' />
            <div>
              <h4 className='font-medium text-green-800'>Up to Date</h4>
              <p className='text-sm text-green-700'>
                No changes detected from {providerName}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Changes List */}
      {hasAnyChanges && (
        <>
          <div className='space-y-3'>
            <p className='text-sm text-gray-600'>
              Select the changes you want to apply:
            </p>

            {comparison.nameChange &&
              renderChangeItem(
                'name',
                'Business Name',
                comparison.nameChange.old as string | undefined,
                comparison.nameChange.new as string | undefined,
              )}

            {comparison.addressChange &&
              renderChangeItem(
                'address',
                'Address',
                comparison.addressChange.old as string | undefined,
                comparison.addressChange.new as string | undefined,
              )}

            {comparison.phoneChange &&
              renderChangeItem(
                'phone',
                'Phone Number',
                comparison.phoneChange.old as string | undefined,
                comparison.phoneChange.new as string | undefined,
              )}

            {comparison.websiteChange &&
              renderChangeItem(
                'website',
                'Website',
                comparison.websiteChange.old as string | undefined,
                comparison.websiteChange.new as string | undefined,
              )}

            {renderOpeningHoursChange(comparison)}
            {renderPhotosChange(comparison)}
          </div>

          {/* Apply Button */}
          <div className='pt-4 border-t'>
            <Button
              label={`Apply ${selectedChanges.size} Change${selectedChanges.size !== 1 ? 's' : ''}`}
              icon={mdiCheck}
              color='success'
              onClick={handleApplyChanges}
              disabled={
                selectedChanges.size === 0 || applyRefreshMutation.isPending
              }
            />
          </div>
        </>
      )}
    </div>
  );
};
